import { useNavigate } from 'react-router-dom';

import { Box, Button, SxProps, Theme } from '@mui/material';
import { ArrowRightCircleIcon } from 'lucide-react';

import { useLanguage } from '../../../hooks';
import { FilterEmptyState } from '../../uiComponents/CommonComponents/FilterEmptyState';
import { LocationsCreateButton } from '../actionButtons/LocationsCreateButton';

const customStyles: Record<string, SxProps<Theme>> = {
  searchEmptyStateContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: '32px 24px',
  },
};

interface LocationSelectorEmptyStateProps {
  variant?: 'searchWithNoResults' | 'noLocationsCreated';
  showAltEmptyState?: boolean;
  hasOldLocations?: boolean;
}

const LocationSelectorEmptyState = ({
  variant,
  showAltEmptyState = false,
  hasOldLocations = false,
}: LocationSelectorEmptyStateProps) => {
  const lang = useLanguage();
  const navigate = useNavigate();

  if (showAltEmptyState) {
    if (variant === 'searchWithNoResults') {
      return <FilterEmptyState text={lang.locations.emptyState.noLocations} />;
    }

    return (
      <div className='flex flex-col items-start'>
        <p className='text-[14px] text-gray-500 mb-2 italic'>
          {lang.locations.emptyState.noLocationsCreated}
        </p>
        <LocationsCreateButton variant='emptyStateLocationsSelector2' />
      </div>
    );
  }

  if (variant === 'searchWithNoResults') {
    return (
      <Box sx={customStyles.searchEmptyStateContainer}>
        <FilterEmptyState text={lang.locations.emptyState.noLocations} />
        <p className='text-sm text-gray-400 text-center mt-1 mb-2'>
          {lang.locations.emptyState.suggestion}
        </p>
        <LocationsCreateButton variant='emptyStateLocationsSelector2' />
      </Box>
    );
  }

  return (
    <div className='flex flex-col items-center justify-center py-6 px-6'>
      <div className='mb-8'>
        <img
          src='https://data-scope.s3.us-west-2.amazonaws.com/assets/locations/empty-locations-selector.png'
          alt='Empty state illustration'
          className='w-48 max-w-full'
        />
      </div>
      <div className='mb-4 text-lg font-medium text-center text-neutral-10'>
        {hasOldLocations ? (
          <p>{lang.locations.emptyState.oldLocationsTitle}</p>
        ) : (
          <p>{lang.locations.emptyState.createNewTitle}</p>
        )}
        <p>{lang.locations.emptyState.oldLocationsDescription}</p>
      </div>
      <Button
        variant='contained'
        onClick={() => navigate('/locations')}
      >
        {lang.locations.emptyState.createButton}
        <ArrowRightCircleIcon className='w-4 h-4 ml-2' />
      </Button>
    </div>
  );
};

export { LocationSelectorEmptyState };
